import { create } from 'zustand';
import type { NatosProvider, NatosAccountInfo, NatosNetworkInfo, NatosSignedIntent } from '../types/natos';

export type WalletConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'locked';

export interface DeploymentRecord {
  id: string;
  projectName: string;
  txHash: string;
  network: string;
  timestamp: number;
  status: 'submitted' | 'confirmed' | 'failed';
}

interface WalletState {
  /** Current connection status */
  connectionStatus: WalletConnectionStatus;
  /** The injected provider reference — null until detected */
  provider: NatosProvider | null;
  /** Active account after connect */
  activeAccount: NatosAccountInfo | null;
  /** All accounts accessible from the wallet */
  accounts: NatosAccountInfo[];
  /** Current network info */
  network: NatosNetworkInfo | null;
  /** Recent deployment history (session-scoped) */
  deployments: DeploymentRecord[];
  /** Last error (cleared on next action) */
  error: string | null;

  // ----- Actions -----

  /** Detect and acquire the injected provider. Resolves when found or after timeout. */
  detectProvider: () => Promise<NatosProvider | null>;
  /** Connect to the wallet (opens extension approval) */
  connect: () => Promise<boolean>;
  /** Disconnect the wallet and reset state */
  disconnect: () => void;
  /** Refresh accounts and network from the connected wallet */
  refresh: () => Promise<void>;
  /** Sign an arbitrary message via the wallet */
  signMessage: (message: string, format?: 'utf8' | 'hex') => Promise<{ signature: string; public_key: string } | null>;
  /** Sign an intent (transaction) via the wallet */
  signIntent: (intent: Record<string, unknown>) => Promise<NatosSignedIntent | null>;
  /** Sign and submit an intent in one step */
  signAndSubmit: (intent: Record<string, unknown>, projectName?: string) => Promise<{ txHash: string } | null>;
  /** Subscribe to wallet provider events (call once after connect) */
  subscribe: () => void;
  /** Unsubscribe from wallet events */
  unsubscribe: () => void;
  /** Add a deployment to history */
  addDeployment: (record: DeploymentRecord) => void;
  /** Clear error */
  clearError: () => void;
}

/** Wait for the natos provider to appear on window (injected by content script) */
function waitForProvider(timeoutMs = 3000): Promise<NatosProvider | null> {
  if (window.natos?.isNatos) return Promise.resolve(window.natos);

  return new Promise<NatosProvider | null>((resolve) => {
    const check = () => {
      if (window.natos?.isNatos) {
        cleanup();
        resolve(window.natos);
      }
    };
    const interval = setInterval(check, 150);
    const timeout = setTimeout(() => { cleanup(); resolve(null); }, timeoutMs);
    const onReady = () => check();
    window.addEventListener('natos:ready', onReady);
    window.addEventListener('natos#initialized', onReady);
    const cleanup = () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.removeEventListener('natos:ready', onReady);
      window.removeEventListener('natos#initialized', onReady);
    };
    check(); // immediate first try
  });
}

// Persistent event handler refs so we can unsubscribe later
let onAccountsChanged: ((data?: unknown) => void) | null = null;
let onDisconnect: ((data?: unknown) => void) | null = null;
let onLocked: ((data?: unknown) => void) | null = null;
let onUnlocked: ((data?: unknown) => void) | null = null;

export const useWalletStore = create<WalletState>()((set, get) => ({
  connectionStatus: 'disconnected',
  provider: null,
  activeAccount: null,
  accounts: [],
  network: null,
  deployments: [],
  error: null,

  detectProvider: async () => {
    const provider = await waitForProvider();
    if (provider) {
      set({ provider });
    }
    return provider;
  },

  connect: async () => {
    set({ connectionStatus: 'connecting', error: null });

    let provider = get().provider;
    if (!provider) {
      provider = await waitForProvider();
      if (!provider) {
        set({ connectionStatus: 'disconnected', error: 'Natos Wallet extension not detected.' });
        return false;
      }
      set({ provider });
    }

    try {
      const result = await provider.connect();
      const active = result.activeAccount ?? result.accounts[0] ?? null;

      let network: NatosNetworkInfo | null = null;
      try {
        network = await provider.getNetwork();
      } catch {
        // non-fatal
      }

      set({
        connectionStatus: 'connected',
        activeAccount: active,
        accounts: result.accounts,
        network,
        error: null,
      });

      get().subscribe();
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Connection failed';
      set({ connectionStatus: 'disconnected', error: msg });
      return false;
    }
  },

  disconnect: () => {
    get().unsubscribe();
    set({
      connectionStatus: 'disconnected',
      provider: null,
      activeAccount: null,
      accounts: [],
      network: null,
      deployments: [],
      error: null,
    });
  },

  refresh: async () => {
    const provider = get().provider;
    if (!provider || get().connectionStatus !== 'connected') return;

    try {
      const [accounts, active, network] = await Promise.all([
        provider.getAccounts(),
        provider.getActiveAccount(),
        provider.getNetwork(),
      ]);
      set({
        accounts: accounts as NatosAccountInfo[],
        activeAccount: active,
        network,
      });
    } catch {
      // Wallet may have locked
    }
  },

  signMessage: async (message, format = 'utf8') => {
    const provider = get().provider;
    if (!provider || get().connectionStatus !== 'connected') {
      set({ error: 'Wallet not connected' });
      return null;
    }
    try {
      set({ error: null });
      const result = await provider.signMessage(message, format);
      return result;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Signing failed' });
      return null;
    }
  },

  signIntent: async (intent) => {
    const provider = get().provider;
    if (!provider || get().connectionStatus !== 'connected') {
      set({ error: 'Wallet not connected' });
      return null;
    }
    try {
      set({ error: null });
      return await provider.signIntent(intent);
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Signing failed' });
      return null;
    }
  },

  signAndSubmit: async (intent, projectName) => {
    const provider = get().provider;
    if (!provider || get().connectionStatus !== 'connected') {
      set({ error: 'Wallet not connected' });
      return null;
    }
    try {
      set({ error: null });
      const result = await provider.signAndSubmit(intent);
      if (result?.txHash) {
        get().addDeployment({
          id: crypto.randomUUID(),
          projectName: projectName ?? 'Unknown',
          txHash: result.txHash,
          network: get().network?.network ?? get().network?.id ?? 'unknown',
          timestamp: Date.now(),
          status: 'submitted',
        });
      }
      return result;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Transaction failed' });
      return null;
    }
  },

  subscribe: () => {
    const provider = get().provider;
    if (!provider) return;

    // Clean up any existing listeners first
    get().unsubscribe();

    onAccountsChanged = (data) => {
      const accounts = (data as { accounts?: NatosAccountInfo[] })?.accounts;
      if (accounts) {
        const active = accounts[0] ?? null;
        set({ accounts, activeAccount: active });
      }
    };

    onDisconnect = () => {
      set({ connectionStatus: 'disconnected', activeAccount: null, accounts: [] });
    };

    onLocked = () => {
      set({ connectionStatus: 'locked' });
    };

    onUnlocked = () => {
      set({ connectionStatus: 'connected' });
      get().refresh();
    };

    provider.on('accountsChanged', onAccountsChanged);
    provider.on('disconnect', onDisconnect);
    provider.on('locked', onLocked);
    provider.on('unlocked', onUnlocked);
  },

  unsubscribe: () => {
    const provider = get().provider;
    if (!provider) return;
    if (onAccountsChanged) { provider.off('accountsChanged', onAccountsChanged); onAccountsChanged = null; }
    if (onDisconnect) { provider.off('disconnect', onDisconnect); onDisconnect = null; }
    if (onLocked) { provider.off('locked', onLocked); onLocked = null; }
    if (onUnlocked) { provider.off('unlocked', onUnlocked); onUnlocked = null; }
  },

  addDeployment: (record) => {
    set(s => ({ deployments: [record, ...s.deployments] }));
  },

  clearError: () => set({ error: null }),
}));
